import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { toast } from "sonner";
import { ActivitiesSearchResult, Department, User, System, ActivitiesData, Activity, Procedure, Training, JobRole, NewUser, JobRoleTitle, JobRoleLogEntry, NewDepartment, TrainingPlan, ExternalPartner, Policy, PolicyAssignment, Memo, MemoAssignment, ActivityLink, ProcedureCheck, Approvers, ActivityLogEntry, Qualification, QualificationType, QualificationGroup, QualificationPreset, EmploymentEntity, JobTitle, JobRoleLocation, JobGrade, TrainingPlanTemplate } from "../types";
import { useAuth } from "./AuthContext";
import { deleteNestedObjFromMainObj, updateFromMainObj, updateMainObj, deleteFromMainObj } from "../utils/helpers";
import api from "../utils/api";

export interface PaginationState {
  total: number;
  page: number;
  limit: number;
  pages: number;
}

interface ActivityUpdateResponse {
  success: boolean;
  message?: string;
  activity: Activity;
  procedure: Procedure;
  activities: Activity[];
}

type ActivitySearchParams = {
  name?: string;
  procedureId?: string;
  userId?: string;
  page?: number;
  limit?: number;
  responsibleUserId?: string;
};

// DataContext Type
interface DataContextType {
  departments: Department[];
  users: User[];
  systems: System[];
  activities: Activity[];
  activitiesData: ActivitiesData | undefined;
  procedures: Procedure[];
  trainings: Training[];
  jobRoles: JobRole[];
  trainingPlans: TrainingPlan[];
  trainingPlanTemplates: TrainingPlanTemplate[];
  externalPartners: ExternalPartner[];
  policies: Policy[];
  policyAssignments: PolicyAssignment[];
  memos: Memo[];
  approvers: Approvers[];
  managerUsers: User[];
  selectionUsers: User[];
  fetchApprovers: () => Promise<void>;
  fetchManagerUsers: () => Promise<void>;
  fetchSelectionUsers: () => Promise<void>;
  fetchProcedures: () => Promise<void>;
  setMemos: React.Dispatch<React.SetStateAction<Memo[]>>;

  memoAssignments: MemoAssignment[];
  refreshMemos: () => Promise<void>;
  refreshPolicies: () => Promise<void>;
  // Memo Management Functions
  addMemo: (memo: Omit<Memo, 'id' | 'createdOn' | 'lastUpdated' | 'activityLog' | 'archived' | 'deleted'>) => Promise<Memo>;
  updateMemo: (id: string, updates: Partial<Memo>) => Promise<void>;
  deleteMemo: (id: string) => Promise<void>;
  fetchMemos: () => Promise<void>;
  addUser: (user: NewUser) => Promise<void>;
  updateUser: (id: string, updates: Partial<Omit<User, "id" | "createdAt" | "updatedAt">>) => Promise<User | undefined>;
  deleteUser: (id: string) => Promise<void>;
  archiveUser: (id: string, userId: string) => Promise<void>;
  unarchiveUser: (id: string, userId: string) => Promise<void>;
  addDepartment: (department: NewDepartment) => Promise<void>;
  updateDepartment: (id: string, updates: Partial<Department>) => Promise<void>;
  deleteDepartment: (id: string) => Promise<void>;
  assignDepartmentManager: (departmentId: string, managerId: string) => Promise<void>;
  archiveDepartment: (id: string, userId: string) => Promise<void>;
  unarchiveDepartment: (id: string, userId: string) => Promise<void>;
  addSystem: (system: Omit<System, "id">) => Promise<void>;
  updateSystem: (id: string, updates: Partial<System>) => Promise<void>;
  deleteSystem: (id: string) => Promise<void>;
  searchActivities: (searchParams: ActivitySearchParams) => Promise<ActivitiesSearchResult>;
  addActivity: (activity: any) => Promise<Activity>;
  addActivityLink: (activityId: any, link: ActivityLink) => Promise<void>;
  updateActivity: (procedureId: string, activityId: string, updates: Partial<Activity>, updateState?: boolean) => Promise<ActivityUpdateResponse>;
  updateActivityLink: (activityId: string, updates: Partial<ActivityLink>) => Promise<void>;
  deleteActivity: (procedureId: string, activityId: string) => Promise<void>;
  deleteActivityLink: (activityId: string, linkId: string) => Promise<void>;
  addProcedure: (procedure: Omit<Procedure, "id">) => Promise<void>;
  updateProcedure: (id: string, updates: Partial<Procedure>, showToast: boolean) => Promise<void>;
  deleteProcedureLink: (id: string, linkId: string) => Promise<void>;
  deleteProcedure: (id: string) => Promise<void>;
  addTraining: (training: Omit<Training, "id">) => void;
  updateTraining: (id: string, updates: Partial<Training>, currentUser: User) => void;
  deleteTraining: (id: string) => void;
  mapJobRole: (jobRole: any) => JobRole;
  addJobRole: (jobRole: any) => Promise<JobRole>;
  updateJobRole: (id: string, updates: Partial<JobRole>) => Promise<void>;
  deleteJobRole: (id: string) => Promise<void>;
  archiveJobRole: (id: string, userId: string, reason?: string) => Promise<{ success: boolean; affectedUsers?: User[] }>;
  unarchiveJobRole: (id: string, userId: string) => Promise<void>;
  archiveProcedure: (id: string, reason?: string) => Promise<void>;
  unarchiveProcedure: (id: string, userId: string) => Promise<void>;
  assignUserDepartments: (userId: string, departments: string[], primaryDepartment: string) => Promise<User | undefined>;
  addTrainingPlan: (plan: Partial<TrainingPlan>) => Promise<void>;
  updateTrainingPlan: (id: string, updates: Partial<TrainingPlan>, showToast?: boolean, isDelete?: boolean) => Promise<void>;
  addTrainingPlanTemplate: (template: Partial<TrainingPlanTemplate>) => Promise<void>;
  updateTrainingPlanTemplate: (id: string, updates: Partial<TrainingPlanTemplate>) => Promise<void>;
  archiveTrainingPlanTemplate: (id: string, reason?: string) => Promise<void>;
  unarchiveTrainingPlanTemplate: (id: string) => Promise<void>;
  updateTrainingProcedureCheck: (planId: string, procedureId: string, updates: Partial<ProcedureCheck>, showToast?: boolean) => Promise<void>;
  // Policy Management Functions
  addPolicy: (policy: Omit<Policy, "id" | "createdDate" | "lastUpdated" | "version" | "activityLog">) => Promise<Policy>;
  updatePolicy: (id: string, updates: Partial<Policy>, showToast?: boolean) => Promise<void>;
  updatePolicyInState: (id: string, updatedPolicy: Policy) => void;
  deletePolicy: (id: string) => Promise<void>;
  archivePolicy: (id: string, reason?: string) => Promise<boolean>;
  unarchivePolicy: (id: string) => Promise<void>;
  getPoliciesForUser: (userId: string) => Policy[];
  markPolicyAsRead: (policyId: string, userId: string) => Promise<void>;
  signPolicy: (policyId: string, signatureData: { signatureType: 'Draw' | 'Type'; signatureData: string }) => Promise<void>;
  addUsersToPolicy: (policyId: string, userIds: string[]) => Promise<void>;
  addUsersToMemo: (memoId: string, userIds: string[]) => Promise<void>;
  removeUsersFromPolicy: (policyId: string, userId: string[]) => Promise<void>;
  removeUsersFromMemo: (memoId: string, userId: string[]) => Promise<void>;
  fetchPolicyAssignments: (policyId: string) => Promise<void>;
  refreshPolicyAssignments: (policyId: string) => Promise<void>;
  getUserPolicySignature: (policyId: string, userId: string) => any;
  getPolicyAssignments: (policyId: string) => PolicyAssignment[];
  // Assignment Management Functions
  getPoliciesForAssignment: () => Promise<Policy[]>;
  createPolicyAssignments: (policyId: string, userIds: string[], assignmentType?: string) => Promise<void>;
  getAssignmentSummary: (policyId: string) => Promise<any>;
  getAllPolicyAssignments: (filters?: { policyId?: string; userId?: string; status?: string }) => Promise<any[]>;
  // Helper functions
  getDepartmentName: (id: string) => string;
  getUserName: (id: string) => string;
  getPolicyById: (id: string) => Policy | undefined;
  getUserById: (id: string) => User | undefined;
  getDepartmentByCode: (code: string) => Department | undefined;
  getUserPolicySignatures: (userId: string) => any[];
  fetchMemoAssignments: (memoId: string) => Promise<void>;

  refreshUserAssignments: () => Promise<void>;
  getMemosForUser: (userId: string) => Memo[];
  getUserMemoSignature: (memoId: string, userId: string) => any;
  getMemoAssignments: (memoId: string) => MemoAssignment[];
  getMemoById: (id: string) => Memo | undefined;
  markMemoAsRead: (memoId: string, userId: string) => Promise<void>;
  signMemo: (memoId: string, signatureData: { signatureType: 'Draw' | 'Type'; signatureData: string }) => Promise<void>;

  // Qualifications Functions
  fetchQualifications: (filters?: any) => Promise<void>;
  addQualification: (qualification: Partial<Qualification>) => Promise<any>;
  updateQualification: (id: string, updates: Partial<Qualification>) => Promise<any>;
  deleteQualification: (id: string) => Promise<void>;
  restoreQualification: (id: string) => Promise<boolean>;
  fetchQualificationTypes: () => Promise<void>;
  qualificationGroups: QualificationGroup[];
  fetchQualificationGroups: () => Promise<void>;
  qualificationPresets: QualificationPreset[];
  fetchQualificationPresets: () => Promise<void>;
  qualifications: Qualification[];
  qualificationTypes: QualificationType[];
  employmentEntities: EmploymentEntity[];
  fetchEmploymentEntities: () => Promise<void>;
  userRoles: string[];
  fetchUserRoles: () => Promise<void>;
  jobTitles: JobTitle[];
  fetchJobTitles: () => Promise<void>;
  addJobTitle: (name: string) => Promise<JobTitle | undefined>;
  updateJobTitle: (id: string, name: string) => Promise<JobTitle | undefined>;
  deleteJobTitle: (id: string) => Promise<any>;
  jobRoleLocations: JobRoleLocation[];
  fetchJobRoleLocations: () => Promise<void>;
  addJobRoleLocation: (name: string) => Promise<JobRoleLocation | undefined>;
  updateJobRoleLocation: (id: string, name: string) => Promise<JobRoleLocation | undefined>;
  deleteJobRoleLocation: (id: string) => Promise<any>;
  jobGrades: JobGrade[];
  fetchJobGrades: () => Promise<void>;
  addJobGrade: (name: string, salaryRangeMin?: number, salaryRangeMax?: number) => Promise<JobGrade | undefined>;
  updateJobGrade: (id: string, name: string, salaryRangeMin?: number, salaryRangeMax?: number) => Promise<JobGrade | undefined>;
  deleteJobGrade: (id: string) => Promise<any>;
}

const DataContext = createContext<DataContextType | undefined>(undefined);

// Export the useData hook
export const useData = () => {
  const context = useContext(DataContext);
  if (context === undefined) {
    throw new Error("useData must be used within a DataProvider");
  }
  return context;
};

// Export the DataProvider component
export const DataProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [departments, setDepartments] = useState<Department[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [procedures, setProcedures] = useState<Procedure[]>([]);
  const [activities, setActivities] = useState<Activity[]>([]);
  const [systems, setSystems] = useState<System[]>([]);
  const [trainings, setTrainings] = useState<Training[]>([]);
  const [jobRoles, setJobRoles] = useState<JobRole[]>([]);
  const [trainingPlans, setTrainingPlans] = useState<TrainingPlan[]>([]);
  const [trainingPlanTemplates, setTrainingPlanTemplates] = useState<TrainingPlanTemplate[]>([]);
  const [externalPartners, setExternalPartners] = useState<ExternalPartner[]>([]);
  const [policies, setPolicies] = useState<Policy[]>([]);
  // Policy signatures are now embedded in policy assignments - no separate state needed
  const [policyAssignments, setPolicyAssignments] = useState<PolicyAssignment[]>([]);
  const [memos, setMemos] = useState<Memo[]>([]);
  const [activitiesData, setActivitiesData] = useState<ActivitiesData | undefined>();
  const [approvers, setApprovers] = useState<Approvers[]>([]);
  const [managerUsers, setManagerUsers] = useState<User[]>([]);
  const [selectionUsers, setSelectionUsers] = useState<User[]>([]);
  const [memoAssignments, setMemoAssignments] = useState<MemoAssignment[]>([]);
  const [qualifications, setQualifications] = useState<Qualification[]>([]);
  const [qualificationGroups, setQualificationGroups] = useState<QualificationGroup[]>([]);
  const [qualificationPresets, setQualificationPresets] = useState<QualificationPreset[]>([]);
  const [qualificationTypes, setQualificationTypes] = useState<QualificationType[]>([]);
  const [employmentEntities, setEmploymentEntities] = useState<EmploymentEntity[]>([]);
  const [userRoles, setUserRoles] = useState<string[]>([]);
  const [jobTitles, setJobTitles] = useState<JobTitle[]>([]);
  const [jobRoleLocations, setJobRoleLocations] = useState<JobRoleLocation[]>([]);
  const [jobGrades, setJobGrades] = useState<JobGrade[]>([]);
  const { token, isAuthenticated, currentUser, updateCurrentUser } = useAuth();

  const fetchDepartments = async () => {
    try {
      const deptData = await api.get('/api/departments');
      if (deptData.success) {
        const departments = deptData.departments;
        let index = departments.findIndex((department: Department) => department.id == (typeof currentUser?.department === 'object' ? (currentUser?.department as any).id : currentUser?.department));
        if (index > -1) {
          let [obj] = departments.splice(index, 1);
          departments.unshift(obj);
        }
        setDepartments(departments);
      }
    } catch (error) {
      console.error('Error fetching departments:', error);
    }
  }

  const fetchUsers = async () => {
    try {
      const userData = await api.get('/api/users');
      if (userData.success) {
        setUsers(userData.users);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  }

  const fetchManagerUsers = async () => {
    try {
      const userData = await api.get('/api/users?manager_users=true');
      if (userData.success) {
        setManagerUsers(userData.users);
      }
    } catch (error) {
      console.error('Error fetching manager users:', error);
    }
  }

  const fetchSelectionUsers = async () => {
    try {
      const userData = await api.get('/api/users/selection');
      if (userData.success) {
        setSelectionUsers(userData.users);
      }
    } catch (error) {
      console.error('Error fetching selection users:', error);
    }
  }

  const fetchProcedures = async () => {
    try {
      const proceduresData = await api.get('/api/procedures');
      if (proceduresData.success) {
        const finalProcedures = proceduresData.procedures.map((p: { department: { departmentCode: any; }; }) => ({ ...p, departmentId: p.department.departmentCode }));
        setProcedures(finalProcedures);
      }
    } catch (error) {
      console.error('Error fetching procedures:', error);
    }
  }

  // Fetch activities
  const fetchActivities = async () => {
    try {
      const activitiesData = await api.get('/api/activities');
      if (activitiesData.success) {
        setActivitiesData({
          ...activitiesData
        });
        setActivities(activitiesData.activities);
      }
    } catch (error) {
      console.error('Error fetching activities:', error);
    }
  };

  const fetchSystems = async () => {
    try {
      const systemsData = await api.get('/api/systems');
      if (systemsData.success) {
        setSystems(systemsData.systems);
      }
    } catch (error) {
      console.error('Error fetching systems:', error);
    }
  }

  const fetchJobRoles = async () => {
    try {
      // The list endpoint is paginated and caps `limit` at 100. Requesting a
      // single page silently truncated every job-role picker in the app — this
      // tenant has 350+ roles, so page through until a short page comes back.
      const fetchAllRoles = async (archived: boolean) => {
        const pageSize = 100;
        const roles = [];

        for (let page = 1; page <= 20; page += 1) {
          const data = await api.get(`/api/jobroles?archived=${archived}&limit=${pageSize}&page=${page}`);
          const batch = (data && data.roles) || [];
          roles.push(...batch);
          if (batch.length < pageSize) break;
        }

        return roles;
      };

      const [activeRoles, archivedRoles] = await Promise.all([fetchAllRoles(false), fetchAllRoles(true)]);

      const allBackendRoles = [...activeRoles, ...archivedRoles];

      if (allBackendRoles.length > 0) {
        // Map backend roles to frontend format
        const frontendRoles = allBackendRoles.map(role => ({
          ...role,
          id: role.id,
          title: role.roleName as JobRoleTitle,
          description: role.description || "",
          departmentId: role.department ? role.department.id : null,
          outcomes: role.outcomes || [],
          keyDuties: role.keyDuties || [],
          archived: role.archived || false,
          archivedBy: role.archivedBy || "",
          archivedOn: role.archivedOn || "",
          archivedReason: role.archivedReason || "",
          activityLogs: role.activityLogs || [],
          requiredCertificatesAndLicenses: role.requiredCertificatesAndLicenses || [],
          preferredQualifications: role.preferredQualifications || [],
          jobGradeId: role.jobGrade ? role.jobGrade.id : undefined,
          jobGrade: role.jobGrade
        }));

        setJobRoles(frontendRoles);
      }
    } catch (error) {
      console.error('Error fetching job roles:', error);
    }
  }

  const fetchExternalPartners = async () => {
    try {
      const partnersData = await api.get('/api/external-partners');
      // The endpoint returns an array directly based on original code, but check if it's wrapped
      // Original code: setExternalPartners(partnersData.filter((partner: ExternalPartner) => partner.active));
      // Assuming api.get returns the parsed JSON which IS the array.
      if (Array.isArray(partnersData)) {
        setExternalPartners(partnersData.filter((partner: ExternalPartner) => partner.active));
      }
    } catch (error) {
      console.error('Error fetching external partners:', error);
    }
  }

  const fetchApprovers = async () => {
    try {
      const data = await api.get('/api/users/approvers');
      if (data.success) {
        setApprovers(data.users);
      }
    } catch (error) {
      console.error('Error fetching approvers:', error);
    }
  };

  const fetchTrainingPlans = async () => {
    try {
      const data = await api.get('/api/trainingplans');
      if (data.success) {
        setTrainingPlans(data.trainingPlans);
      }
    } catch (error) {
      console.error('Error fetching training plans:', error);
    }
  };

  const mapTemplate = (template: any) => ({
    ...template,
    jobRole: template.jobRole ? {
      ...template.jobRole,
      title: template.jobRole.roleName
    } : undefined
  });

  const fetchTrainingPlanTemplates = async () => {
    try {
      const data = await api.get('/api/trainingplan-templates');
      if (data.success) {
        setTrainingPlanTemplates(data.templates.map(mapTemplate));
      }
    } catch (error) {
      console.error('Error fetching training plan templates:', error);
    }
  };

    const resetState = () => {
    setDepartments([]);
    setUsers([]);
    setProcedures([]);
    setActivities([]);
    setSystems([]);
    setTrainings([]);
    setJobRoles([]);
    setTrainingPlans([]);
    setExternalPartners([]);
    setPolicies([]);
    setPolicyAssignments([]);
    setMemos([]);
    setActivitiesData(undefined);
    setApprovers([]);
    setManagerUsers([]);
    setMemoAssignments([]);
    setQualifications([]);
    setQualificationTypes([]);
    setQualificationGroups([]);
    setQualificationPresets([]);
    setSelectionUsers([]);
    setJobTitles([]);
  };

  // Combined fetch function that runs when authentication is ready
  const fetchInitialData = async () => {
    const storedToken = localStorage.getItem("token");
    if (!isAuthenticated || !storedToken) {
      console.log("🔄 Waiting for authentication...");
      return;
    }

    try {
      fetchDepartments();
      fetchUsers();
      fetchProcedures();
      fetchActivities();
      fetchSystems();
      fetchJobRoles();
      fetchExternalPartners();
      fetchTrainingPlans();
      fetchTrainingPlanTemplates();
      fetchQualifications();
      fetchQualificationTypes();
      fetchQualificationGroups();
      fetchQualificationPresets();
      fetchSelectionUsers();
      fetchUserRoles();
      fetchJobTitles();
      fetchJobRoleLocations();
      fetchJobGrades();

      //  console.log("✅ Initial data fetch completed successfully");
    } catch (error: any) {
      console.error("❌ Error fetching initial data:", error);
      if (error.message !== "Token expired") {
        toast.error("Error loading data");
      }
    }
  };

  // Single useEffect that triggers the fetch when authentication is ready
  useEffect(() => {
    if (isAuthenticated && token) {
      fetchInitialData();
    } else if (!isAuthenticated) {
      resetState();
    }
  }, [isAuthenticated, token]);

  const addDepartment = async (department: NewDepartment) => {
    try {
      console.log('[addDepartment] Sending department:', department);
      const data = await api.post('/api/departments', department);

      setDepartments(prev => [...prev, data.department]);
    } catch (error: any) {
      console.error("Error adding department:", error);
    }
  };

  const updateDepartment = async (id: string, updates: Partial<Department>) => {
    try {
      if (updates.departmentManager) {
        await api.patch(`/api/departments/${id}/manager`, { managerId: updates.departmentManager });
        delete updates.departmentManager;
      }

      const data = await api.put(`/api/departments/${id}`, updates);

      setDepartments(prev => prev.map(d => d.id === id ? data.department : d));
    } catch (error: any) {
      console.error("Error updating department:", error);
    }
  };

  const deleteDepartment = async (id: string) => {
    try {
      await api.delete(`/api/departments/${id}`);

      setDepartments(prev => prev.filter(d => d.id !== id));
      toast.success("Department deleted successfully");
    } catch (error: any) {
      console.error("Delete department error:", error);
    }
  };

  const assignDepartmentManager = async (departmentId: string, managerId: string) => {
    try {
      await api.patch(`/api/departments/${departmentId}/manager`, { managerId });

      // Find the selected user from the users array
      const selectedUser = users.find(u => u.id == managerId);

      // Update local state after successful API call
      setDepartments(prev => prev.map(d => {
        if (d.id === departmentId) {
          return {
            ...d,
            departmentManager: selectedUser
          };
        }
        return d;
      }));

      // If the user was promoted to Manager, update the users state
      if (selectedUser && selectedUser.userRole !== "Manager") {
        setUsers(prev => prev.map(u => {
          if (u.id === managerId) {
            return { ...u, userRole: "Manager" };
          }
          return u;
        }));
      }
    } catch (error) {
      console.error("Error assigning department manager:", error);
      throw error;
    }
  };

  // User functions
  const addUser = async (user: NewUser) => {
    try {
      // Validate required fields
      if (!user.name || !user.email || !user.password || !user.userRole || !user.jobRole || !user.department) {
        throw new Error("Missing required fields");
      }

      console.log('Sending user data:', {
        ...user,
        password: user.password ? '[REDACTED]' : undefined
      });

      const data = await api.post('/api/users', user);

      setUsers(prev => [...prev, data.user]);
      toast.success("User added successfully");
    } catch (error: any) {
      console.error("Add user error details:", {
        name: error.name,
        message: error.message,
        stack: error.stack
      });
      // toast.error(error.message || "Failed to add user");
      throw error;
    }
  };

  const updateUser = async (id: string, updates: Partial<Omit<User, "id" | "createdAt" | "updatedAt">>) => {
    try {
      const data = await api.put(`/api/users/${id}`, updates);

      const updatedUser = data.user;
      updatedUser.manager = data.user.manager ? data.user.manager.id : null;

      setUsers(prev => prev.map(u => u.id == id ? updatedUser : u));

      // managerUsers/selectionUsers are separate projections of the same rows.
      // Refresh the edited user in each so every screen picks up the change
      // without a page reload, keeping each entry's own shape.
      const refreshCachedUser = (list: User[]) =>
        list.map(u => {
          if (u.id != id) return u;
          const refreshed: Record<string, any> = { ...u };
          Object.keys(u).forEach(key => {
            if (key in updatedUser) refreshed[key] = updatedUser[key];
          });
          return refreshed as User;
        });
      setManagerUsers(refreshCachedUser);
      setSelectionUsers(refreshCachedUser);

      // Editing your own account: keep the session identity in step too.
      if (currentUser && String(currentUser.id) === String(id)) {
        updateCurrentUser({ name: updatedUser.name, email: updatedUser.email });
      }

      return updatedUser;
    } catch (error: any) {
      console.error("Update user error:", error);
      throw error;
    }
  };

  const deleteUser = async (id: string) => {
    try {
      await api.delete(`/api/users/${id}`);

      setUsers(prev => prev.filter(u => u.id !== id));
      toast.success("User deleted successfully");
    } catch (error: any) {
      console.error("Delete user error:", error);
      // toast.error(error.message || "Failed to delete user");
    }
  };

  // Procedure functions
  const addProcedure = async (procedure: Omit<Procedure, "id">) => {
    try {
      const data = await api.post('/api/procedures', {
        ...procedure,
        createdBy: currentUser?.id,
        createdOn: new Date().toISOString().split('T')[0]
      });

      const newProcedure = { ...data.procedure, departmentId: data.procedure.department.departmentCode };
      setProcedures(prev => [...prev, newProcedure]);

      toast.success("Procedure added successfully");
    } catch (error: any) {
      console.error("Add procedure error:", error);
      // toast.error(error.message || "Failed to add procedure");
      throw error;
    }
  };

  const updateProcedure = async (id: string, updates: Partial<Procedure>, showToast: boolean = true) => {
    try {
      console.log('[DataContext] Updating procedure:', {
        id,
        updates,
        currentUser
      });

      // Clean up activity log entries to ensure valid ObjectIds
      let cleanUpdates = { ...updates };

      const data = await api.put(`/api/procedures/${id}`, {
        ...cleanUpdates,
        updatedBy: currentUser?.id
      });

      console.log('[DataContext] Update procedure response:', {
        success: data.success,
        data
      });

      if (data.success && data.procedure) {
        setProcedures(updateMainObj(procedures, id, data.procedure));
      }

      if (showToast) {
        toast.success("Procedure updated successfully");
      }
    } catch (error: any) {
      console.error("[DataContext] Update procedure error:", error);
      // toast.error(error.message || "Failed to update procedure");
      throw error;
    }
  };

  const deleteProcedureLink = async (id: string, linkId: string) => {
    try {
      await api.delete(`/api/procedures/${id}/links/${linkId}`);
      toast.success("Procedure link deleted successfully");
    } catch (error: any) {
      console.error("Delete procedure error:", error);
      // toast.error(error.message || "Failed to delete procedure");
    }
  };

  const deleteProcedure = async (id: string) => {
    try {
      await api.delete(`/api/procedures/${id}`);

      setProcedures(prev => prev.filter(p => p.id !== id));
      toast.success("Procedure deleted successfully");
    } catch (error: any) {
      console.error("Delete procedure error:", error);
      // toast.error(error.message || "Failed to delete procedure");
    }
  };

  const searchAbortControllerRef = useRef<AbortController | null>(null);
  const searchActivities = async (searchParams: ActivitySearchParams) => {
    if (searchAbortControllerRef.current) {
      searchAbortControllerRef.current.abort();
      // console.log("Aborted previous search request.");
    }

    const controller = new AbortController();
    searchAbortControllerRef.current = controller;

    try {
      // Filter out any undefined or null properties from the search params
      const filteredParams: Record<string, string> = {};
      for (const key in searchParams) {
        const value = searchParams[key as keyof ActivitySearchParams];
        if (value) {
          filteredParams[key] = String(value); // Ensure all params are strings
        }
      }

      const params = new URLSearchParams(filteredParams);
      // Pass the signal to api helper
      const result = await api.get(`/api/activities/search?${params.toString()}`, {
        signal: controller.signal
      });

      searchAbortControllerRef.current = null;
      // You can return the results if the calling component needs them
      return result;

    } catch (error: any) {
      if (error.name === 'AbortError') {
        // Ignore abort errors
        throw error;
      }
      console.error("Error searching for activities:", error);
      const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred';
      // toast.error(`Search failed: ${errorMessage}`); // api helper toasts

      searchAbortControllerRef.current = null;
      throw error;
    }
  };

  // Activity functions
  const addActivity = async (activity: any) => {
    try {
      console.log('[DataContext] Sending activity data:', {
        procedureId: activity.procedureId,
        activityName: activity.activityName,
        description: activity.description || "",
        sequence: activity.sequence,
        raci: activity.raci,
        howTo: activity.howTo || "",
        systemsUsed: activity.systemsUsed || [],
        links: activity.links || [],
        createdBy: currentUser?.id,
        isActive: true
      });

      const result = await api.post('/api/activities', {
        procedureId: activity.procedureId,
        activityName: activity.activityName,
        description: activity.description || "",
        sequence: activity.sequence,
        raci: activity.raci,
        howTo: activity.howTo || "",
        systemsUsed: activity.systemsUsed || [],
        links: activity.links || [],
        createdBy: currentUser?.id,
        isActive: true
      });

      console.log('[DataContext] Activity created on backend:', result);

      // Update the procedure in our state with the new activity
      setProcedures(prev => prev.map(proc => {
        if (proc.id == activity.procedureId) {
          return {
            ...proc,
            activities: [...(proc.activities || []), result.activity]
          };
        }
        return proc;
      }));

      // Update activities state
      setActivities(prev => [...prev, {
        ...result.activity,
        procedure: activity.procedure
      }]);

      toast.success("Activity created successfully");
      return result.activity;
    } catch (error: any) {
      console.error("Error creating activity:", error);
      // toast.error(`Error creating activity: ${error.message}`);
      throw error;
    }
  };

  const addActivityLink = async (activityId: any, link: Partial<ActivityLink>): Promise<void> => {
    try {
      const data = await api.post(`/api/activities/${activityId}/links`, link);

      if (!data.success) {
        throw new Error(data.message || 'Failed to add link to activity');
      }

      // Update activities state with the updated activity
      if (data.link) {
        setActivities(currentActivities =>
          currentActivities.map(activity => {

            if (activity.id == activityId) {
              return {
                ...activity,
                links: [...(activity.links || []), data.link]
              };
            }

            return activity;
          })
        );
      }

      return data;
    } catch (error) {
      console.error('[DataContext] Error updating activity:', error);
      const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred';
      toast.error(`Error updating activity: ${errorMessage}`);
      throw error;
    }
  }

  const updateActivityLink = async (activityId: string, updatedLink: Partial<ActivityLink>) => {
    const { id: linkId, ...linkData } = updatedLink;

    if (!linkId) {
      toast.error("Cannot update link: Link ID is missing.");
      return;
    }

    try {
      const data = await api.put(`/api/activities/${activityId}/links/${linkId}`, linkData);

      if (data.link) {
        setActivities(updateFromMainObj(activities, activityId, 'links', updatedLink));
      }

      // api helper takes care of generic error handling
    } catch (error: any) {
      console.error("Update activity link error:", error);
    }
  };

  const deleteActivityLink = async (id: string, linkId: string) => {
    try {
      await api.delete(`/api/activities/${id}/links/${linkId}`);

      setActivities(deleteNestedObjFromMainObj(activities, id, 'links', linkId));
      // toast.success("Activity link deleted successfully");
    } catch (error: any) {
      console.error("Delete activity link error:", error);
      // toast.error(error.message || "Failed to delete activity link");
    }
  };

  const updateActivity = async (procedureId: string, activityId: string, updates: Partial<Activity>, updateState = true): Promise<ActivityUpdateResponse> => {
    try {
      const data = await api.put<ActivityUpdateResponse>(`/api/activities/${activityId}`, { ...updates, procedureId });

      // Update activities state with the updated activity
      if (updateState && data.activity) {
        setActivities(prev => prev.map(a =>
          a.id == activityId
            ? { ...data.activity, procedure: data.procedure }
            : a
        ));
      }

      // Update procedures state with the updated procedure
      if (updateState && data.procedure) {
        setProcedures(prev => prev.map(p =>
          p.id == procedureId
            ? {
              ...p,
              activities: p.activities?.map(a =>
                a.id == activityId ? data.activity : a
              )
            }
            : p
        ));
      }

      return data;
    } catch (error: any) {
      console.error('[DataContext] Error updating activity:', error);
      throw error;
    }
  };

  const deleteActivity = async (procedureId: string, activityId: string) => {
    try {
      await api.delete(`/api/activities/${procedureId}/${activityId}`);

      // Update both procedures and activities states
      const updatedProcedures = procedures.map(proc => {
        if (proc.id === procedureId) {
          return {
            ...proc,
            activities: (proc.activities || []).filter(act => act.id !== activityId)
          };
        }
        return proc;
      });

      // Update activities state
      setActivities(prev => prev.filter(act => act.id !== activityId));

      setProcedures(updatedProcedures);
      // toast.success("Activity deleted successfully");
    } catch (error) {
      console.error('Error deleting activity:', error);
      const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred';
      toast.error(`Error deleting activity: ${errorMessage}`);
      throw error;
    }
  };

  // Qualification functions
  const fetchQualifications = async (filters: any = {}) => {
    try {
      const params = new URLSearchParams(filters);
      const data = await api.get(`/api/qualifications?${params.toString()}`);
      if (data.success) {
        setQualifications(data.qualifications);
      }
    } catch (error) {
      console.error('Error fetching qualifications:', error);
    }
  };

  const fetchQualificationGroups = useCallback(async () => {
    try {
      const response = await api.get("/api/qualifications/groups/all");
      if (response.success) {
        setQualificationGroups(response.groups);
      }
    } catch (error) {
      console.error("Error fetching qualification groups:", error);
    }
  }, []);

  const fetchQualificationPresets = useCallback(async () => {
    try {
      const data = await api.get('/api/qualifications/presets');
      if (data.success) {
        setQualificationPresets(data.presets);
      }
    } catch (error) {
      console.error("Error fetching qualification presets:", error);
    }
  }, []);

  const fetchEmploymentEntities = async () => {
    try {
      const response = await api.get('/api/employment-entities');
      if (response.success) {
        setEmploymentEntities(response.employmentEntities || []);
      }
    } catch (error) {
      console.error('Error fetching employment entities:', error);
    }
  };

  const fetchUserRoles = async () => {
    try {
      const response = await api.get('/api/users/roles');
      if (response.success) {
        const roles = response.userRoles || [];
        // Ensure default roles are always included as fallback
        const defaultRoles = ['Account Owner', 'Admin', 'Director', 'Senior Manager', 'Manager', 'Supervisor', 'Team Lead', 'Field Worker', 'Worker'];
        const merged = [...new Set([...defaultRoles, ...roles])];
        setUserRoles(merged);
      }
    } catch (error) {
      console.error('Error fetching user roles:', error);
      // Fallback to default roles on error
      setUserRoles(['Account Owner', 'Admin', 'Director', 'Senior Manager', 'Manager', 'Supervisor', 'Team Lead', 'Field Worker', 'Worker']);
    }
  };

  const fetchJobTitles = async () => {
    try {
      const response = await api.get('/api/job-titles');
      if (response.success) {
        setJobTitles(response.jobTitles || []);
      }
    } catch (error) {
      console.error('Error fetching job titles:', error);
    }
  };

  const addJobTitle = async (name: string) => {
    try {
      const response = await api.post('/api/job-titles', { name });
      if (response.success) {
        setJobTitles(prev => [...prev, response.jobTitle]);
        toast.success('Job title added successfully');
        return response.jobTitle;
      }
    } catch (error: any) {
      console.error('Error adding job title:', error);
      throw error;
    }
  };

  const updateJobTitle = async (id: string, name: string) => {
    try {
      const response = await api.put(`/api/job-titles/${id}`, { name });
      if (response.success) {
        setJobTitles(prev => prev.map(jt => jt.id === id ? response.jobTitle : jt));
        toast.success('Job title updated successfully');
        return response.jobTitle;
      }
    } catch (error: any) {
      console.error('Error updating job title:', error);
      throw error;
    }
  };

  const deleteJobTitle = async (id: string) => {
    try {
      const response = await api.delete(`/api/job-titles/${id}`);
      setJobTitles(prev => prev.map(jt => jt.id === id ? { ...jt, deleted: true, deletedOn: new Date().toISOString() } : jt));
      toast.success('Job title deleted successfully');
      return response;
    } catch (error: any) {
      console.error('Error deleting job title:', error);
      throw error;
    }
  };

  const fetchJobRoleLocations = async () => {
    try {
      const response = await api.get('/api/job-role-locations');
      if (response.success) {
        setJobRoleLocations(response.jobRoleLocations || []);
      }
    } catch (error) {
      console.error('Error fetching job role locations:', error);
    }
  };

  const addJobRoleLocation = async (name: string) => {
    try {
      const response = await api.post('/api/job-role-locations', { name });
      if (response.success) {
        setJobRoleLocations(prev => [...prev, response.jobRoleLocation]);
        toast.success('Job role location added successfully');
        return response.jobRoleLocation;
      }
    } catch (error: any) {
      console.error('Error adding job role location:', error);
      throw error;
    }
  };

  const updateJobRoleLocation = async (id: string, name: string) => {
    try {
      const response = await api.put(`/api/job-role-locations/${id}`, { name });
      if (response.success) {
        setJobRoleLocations(prev => prev.map(jl => jl.id === id ? response.jobRoleLocation : jl));
        toast.success('Job role location updated successfully');
        return response.jobRoleLocation;
      }
    } catch (error: any) {
      console.error('Error updating job role location:', error);
      throw error;
    }
  };

  const deleteJobRoleLocation = async (id: string) => {
    try {
      const response = await api.delete(`/api/job-role-locations/${id}`);
      setJobRoleLocations(prev => prev.map(jl => jl.id === id ? { ...jl, deleted: true, deletedOn: new Date().toISOString() } : jl));
      toast.success('Job role location deleted successfully');
      return response;
    } catch (error: any) {
      console.error('Error deleting job role location:', error);
      throw error;
    }
  };

  const fetchJobGrades = async () => {
    try {
      const response = await api.get('/api/job-grades');
      if (response.success) {
        setJobGrades(response.jobGrades || []);
      }
    } catch (error) {
      console.error('Error fetching job grades:', error);
    }
  };

  const addJobGrade = async (name: string, salaryRangeMin?: number, salaryRangeMax?: number) => {
    try {
      const response = await api.post('/api/job-grades', { name, salaryRangeMin, salaryRangeMax });
      if (response.success) {
        setJobGrades(prev => [...prev, response.jobGrade]);
        toast.success('Job grade added successfully');
        return response.jobGrade;
      }
    } catch (error: any) {
      console.error('Error adding job grade:', error);
      throw error;
    }
  };

  const updateJobGrade = async (id: string, name: string, salaryRangeMin?: number, salaryRangeMax?: number) => {
    try {
      const response = await api.put(`/api/job-grades/${id}`, { name, salaryRangeMin, salaryRangeMax });
      if (response.success) {
        setJobGrades(prev => prev.map(jg => jg.id === id ? response.jobGrade : jg));
        toast.success('Job grade updated successfully');
        return response.jobGrade;
      }
    } catch (error: any) {
      console.error('Error updating job grade:', error);
      throw error;
    }
  };

  const deleteJobGrade = async (id: string) => {
    try {
      const response = await api.delete(`/api/job-grades/${id}`);
      setJobGrades(prev => prev.map(jg => jg.id === id ? { ...jg, deleted: true, deletedOn: new Date().toISOString() } : jg));
      toast.success('Job grade deleted successfully');
      return response;
    } catch (error: any) {
      console.error('Error deleting job grade:', error);
      throw error;
    }
  };

  const fetchQualificationTypes = async () => {
    try {
      const data = await api.get('/api/qualifications/types');
      if (data.success) {
        setQualificationTypes(data.types);
      }
    } catch (error) {
      console.error('Error fetching qualification types:', error);
    }
  };

  const addQualification = async (qualification: Partial<Qualification>) => {
    try {
      const data = await api.post('/api/qualifications', qualification);
      if (data.success) {
        setQualifications(prev => [...prev, data.qualification]);
        toast.success(data.message || "Qualification added successfully");
        return data;
      }
    } catch (error) {
      console.error('Error adding qualification:', error);
      throw error;
    }
  };

  const updateQualification = async (id: string, updates: Partial<Qualification>) => {
    try {
      const data = await api.put(`/api/qualifications/${id}`, updates);
      if (data.success) {
        setQualifications(prev => prev.map(q => q.id === id ? data.qualification : q));
        toast.success(data.message || "Qualification updated successfully");
        return data;
      }
    } catch (error) {
      console.error('Error updating qualification:', error);
      throw error;
    }
  };

  const deleteQualification = async (id: string) => {
    try {
      const data = await api.delete(`/api/qualifications/${id}`);
      if (data.success) {
        setQualifications(prev => prev.filter(q => q.id !== id));
        toast.success(data.message || "Qualification moved to recycle bin");
      }
    } catch (error) {
      console.error('Error deleting qualification:', error);
      throw error;
    }
  };

  const restoreQualification = async (id: string) => {
    try {
      const data = await api.post(`/api/qualifications/restore/${id}`, {});
      if (data.success) {
        toast.success(data.message || "Qualification restored successfully");
        // Re-fetch all to ensure full objects are loaded with relations
        await fetchQualifications();
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error restoring qualification:', error);
      throw error;
    }
  };

  // System functions
  const addSystem = async (system: Omit<System, "id">) => {
    try {
      const data = await api.post('/api/systems', system);
      setSystems(prev => [...prev, data.system]);
      toast.success("System added successfully");
    } catch (error: any) {
      console.error("Add system error:", error);
      // toast.error(error.message || "Failed to add system");
      throw error;
    }
  };

  const updateSystem = async (id: string, updates: Partial<System>) => {
    try {
      const data = await api.put(`/api/systems/${id}`, updates);

      setSystems(prev => prev.map(s => s.id === id ? data.system : s));
      toast.success("System updated successfully");
    } catch (error: any) {
      console.error("Update system error:", error);
      // toast.error(error.message || "Failed to update system");
    }
  };

  const deleteSystem = async (id: string) => {
    try {
      await api.delete(`/api/systems/${id}`);

      setSystems(prev => prev.filter(s => s.id !== id));
      toast.success("System deleted successfully");
    } catch (error: any) {
      console.error("Delete system error:", error);
      // toast.error(error.message || "Failed to delete system");
    }
  };

  // Training functions
  const addTraining = (training: Omit<Training, "id">) => {
    const newTraining = { ...training, id: Date.now().toString() };
    setTrainings(prev => [...prev, newTraining]);
    toast.success("Training added successfully");
  };

  const updateTraining = (id: string, updates: Partial<Training>, currentUser: User) => {
    setTrainings(prev => prev.map(t => t.id === id ? { ...t, ...updates } : t));
    toast.success("Training updated successfully");
  };

  const deleteTraining = (id: string) => {
    setTrainings(prev => prev.filter(t => t.id !== id));
    toast.success("Training deleted successfully");
  };

  // Job Role functions
  const mapJobRole = (jobRole: any): JobRole => {
    return {
      id: jobRole.id,
      title: jobRole.roleName || "",
      roleName: jobRole.roleName || "",
      description: jobRole.description,
      departmentId: jobRole.department ? jobRole.department.id : null,
      department: jobRole.department,
      outcomes: jobRole.outcomes,
      keyDuties: jobRole.keyDuties,
      activityLogs: jobRole.activityLogs,
      archived: jobRole.archived,
      archivedOn: jobRole.archivedOn,
      archivedBy: jobRole.archivedBy,
      archivedReason: jobRole.archivedReason,
      attachments: jobRole.attachments || [],
      requiredCertificatesAndLicenses: jobRole.requiredCertificatesAndLicenses || [],
      preferredQualifications: jobRole.preferredQualifications || [],
      salaryRangeMin: jobRole.salaryRangeMin,
      salaryRangeMax: jobRole.salaryRangeMax,
      jobGradeId: jobRole.jobGrade ? jobRole.jobGrade.id : undefined,
      jobGrade: jobRole.jobGrade,
    }
  }

  const addJobRole = async (jobRole: any) => {
    try {
      console.log('Sending job role data:', jobRole);

      const data = await api.post('/api/jobroles', jobRole);

      // Map the response data back to frontend format
      const frontendJobRole: JobRole = {
        id: data.role.id,
        title: data.role.roleName as JobRoleTitle,
        roleName: data.role.roleName as JobRoleTitle,
        description: data.role.description || "",
        departmentId: data.role.department ? data.role.department.id : data.role.departmentId,
        department: data.role.department,
        outcomes: data.role.outcomes || [],
        keyDuties: data.role.keyDuties || [],
        archived: data.role.archived || false,
        archivedBy: data.role.archivedBy || "",
        archivedOn: data.role.archivedOn || "",
        archivedReason: data.role.archivedReason || "",
        activityLogs: data.role.activityLogs || [],
        requiredCertificatesAndLicenses: data.role.requiredCertificatesAndLicenses || [],
        salaryRangeMin: data.role.salaryRangeMin,
        salaryRangeMax: data.role.salaryRangeMax,
        jobGradeId: data.role.jobGrade ? data.role.jobGrade.id : undefined,
        jobGrade: data.role.jobGrade,
      };

      setJobRoles(prev => [...prev, frontendJobRole]);
      return frontendJobRole;
    } catch (error: any) {
      console.error("Add job role error:", error);
      console.error("Error details:", {
        message: error.message,
        stack: error.stack,
        cause: error.cause
      });
      throw error;
    }
  };

  const updateJobRole = async (id: string, updates: Partial<JobRole>) => {
    try {
      // Map frontend JobRole structure to backend API structure
      const backendUpdates: any = {};

      console.log('🔄 Starting job role update:', {
        id,
        updates
      });

      // Map the fields directly as they are in the backend
      if (updates.title) {
        backendUpdates.roleName = updates.title;
      }

      if (updates.departmentId) {
        backendUpdates.departmentId = updates.departmentId; // Changed from department to departmentId
      }

      if (updates.outcomes !== undefined) {
        backendUpdates.outcomes = updates.outcomes;
      }

      if (updates.keyDuties !== undefined) {
        backendUpdates.keyDuties = updates.keyDuties;
      }

      if (updates.archived !== undefined) {
        backendUpdates.isActive = !updates.archived;
      }

      if (updates.activityLogs) {
        // Ensure activity log entries have the correct format
        backendUpdates.activityLog = updates.activityLogs.map((entry: any) => ({
          timestamp: entry.timestamp,
          userId: entry.userId,
          changeDescription: entry.changeDescription
        }));
      }

      if (updates.description !== undefined) {
        console.log('📝 Setting description in backendUpdates:', {
          value: updates.description,
          type: typeof updates.description,
          length: updates.description?.length
        });
        backendUpdates.description = updates.description || "";
      }

      if (updates.requiredCertificatesAndLicenses !== undefined) {
        backendUpdates.requiredCertificatesAndLicenses = updates.requiredCertificatesAndLicenses;
      }

      if (updates.preferredQualifications !== undefined) {
        backendUpdates.preferredQualifications = updates.preferredQualifications;
      }

      if (updates.salaryRangeMin !== undefined) {
        backendUpdates.salaryRangeMin = updates.salaryRangeMin;
      }

      if (updates.salaryRangeMax !== undefined) {
        backendUpdates.salaryRangeMax = updates.salaryRangeMax;
      }

      if (updates.jobGradeId !== undefined) {
        backendUpdates.jobGradeId = updates.jobGradeId || null;
      }

      console.log('📦 Sending updates to backend:', backendUpdates);

      interface JobRoleApiResponse {
        success: boolean;
        message?: string;
        role: {
          id: string;
          roleName: string;
          department: string;
          keyDuties: string[];
          outcomes: string[];
          isActive: boolean;
          activityLogs: ActivityLogEntry[];
        };
      }

      const data = await api.put<JobRoleApiResponse>(`/api/jobroles/${id}`, backendUpdates);

      // Update local state with the response data
      if (data.success && data.role) {
        const updatedRole = mapJobRole(data.role);
        setJobRoles(prev => prev.map(j => j.id === id ? updatedRole : j));
        toast.success("Job Role updated successfully");
      }
    } catch (error: any) {
      console.error("❌ Update job role error:", error);
      throw error;
    }
  };

  const deleteJobRole = async (id: string) => {
    try {
      // First check if any users have this role
      const jobRole = jobRoles.find(role => role.id === id);
      if (!jobRole) {
        throw new Error("Job role not found");
      }

      const usersWithRole = users.filter(user => user.jobRole === jobRole.title);
      if (usersWithRole.length > 0) {
        const userNames = usersWithRole.map(user => user.name).join(", ");
        throw new Error(`This role cannot be deleted because it is occupied by ${userNames}`);
      }

      await api.delete(`/api/jobroles/${id}`);

      // Actually remove the role from the local state
      setJobRoles(prev => prev.filter(j => j.id !== id));
      toast.success("Job role deleted successfully");
    } catch (error: any) {
      console.error("Delete job role error:", error);
      // toast.error(error.message || "Failed to delete job role");
    }
  };

  const archiveJobRole = async (id: string, userId: string, reason?: string) => {
    try {
      const data = await api.patch(`/api/jobroles/${id}/archive`, { archived: true, reason });

      // Check if job role is currently assigned to users
      const assignedUsers = users.filter(user => user.jobRole === id);
      if (assignedUsers.length > 0) {
        return { success: false, affectedUsers: assignedUsers };
      }

      // Update the job role data from the response
      if (data.role) {
        setJobRoles(prev => prev.map(j => j.id === id ? {
          ...j,
          archived: true,
          archivedBy: userId,
          archivedOn: new Date().toISOString(),
          archivedReason: reason
        } : j));
      }
      toast.success("Job Role archived successfully");
      return { success: true };
    } catch (error: any) {
      console.error("Error archiving job role:", error);
      if (error.message !== "Token expired") {
        // toast.error("Failed to archive job role");
      }
      return { success: false };
    }
  };

  const unarchiveJobRole = async (id: string, userId: string) => {
    try {
      const data = await api.patch(`/api/jobroles/${id}/archive`, { archived: false });

      // Update the job role data from the response
      if (data.role) {
        setJobRoles(prev => prev.map(j => j.id === id ? {
          ...j,
          archived: false,
          archivedOn: undefined,
          archivedBy: undefined,
          archivedReason: undefined
        } : j));
      }
      toast.success("Job Role unarchived successfully");
    } catch (error: any) {
      console.error("Error unarchiving job role:", error);
      if (error.message !== "Token expired") {
        // toast.error("Failed to unarchive job role");
      }
    }
  };

  const archiveProcedure = async (id: string, reason?: string) => {
    try {
      await api.patch(`/api/procedures/${id}/archive`, { archived: true, archivedBy: currentUser?.id, archivedReason: reason });

      setProcedures(updateMainObj(procedures, id, {
        archived: true,
        archivedOn: new Date().toISOString(),
        archivedBy: currentUser,
        archivedReason: reason
      }));

      toast.success("Procedure archived successfully");
    } catch (error: any) {
      console.error("Archive procedure error:", error);
      // toast.error(error.message || "Failed to archive procedure");
    }
  };

  const unarchiveProcedure = async (id: string, userId: string) => {
    try {
      await api.patch(`/api/procedures/${id}/archive`, { archived: false });

      setProcedures(prev => prev.map(p => p.id === id ? {
        ...p,
        archived: false,
        archivedOn: undefined,
        archivedBy: undefined,
        archivedReason: undefined
      } : p));
      toast.success("Procedure unarchived successfully");
    } catch (error: any) {
      console.error("Unarchive procedure error:", error);
      // toast.error(error.message || "Failed to unarchive procedure");
    }
  };

  const assignUserDepartments = async (userId: string, departments: string[], primaryDepartment: string): Promise<User | undefined> => {
    try {
      const data = await api.put(`/api/users/${userId}/departments`, { departments, primaryDepartment });

      data.user.departments = data.user.departments.map((d: Department) => d.departmentCode);

      // Update local state
      setUsers(prev => prev.map(u => u.id === userId ? data.user : u));
      toast.success("Departments updated successfully");

      return data.user;
    } catch (error: any) {
      console.error("Update user departments error:", error);
      // toast.error(error.message || "Failed to update user departments");

      return undefined;
    }
  };

  const archiveDepartment = async (id: string, userId: string) => {
    try {
      const data = await api.patch(`/api/departments/${id}/archive`, { archived: true });

      // Refresh the department data from the response to get the populated archivedBy field
      if (data.department) {
        setDepartments(prev => prev.map(d => d.id === id ? data.department : d));
      } else {
        setDepartments(prev => prev.map(d => d.id === id ? { ...d, archived: true, archivedBy: userId, archivedOn: new Date().toISOString() } : d));
      }
      toast.success("Department archived successfully");
    } catch (error: any) {
      console.error("Error archiving department:", error);
      if (error.message !== "Token expired") {
        toast.error("Failed to archive department");
      }
    }
  };

  const unarchiveDepartment = async (id: string, userId: string) => {
    try {
      const data = await api.patch(`/api/departments/${id}/archive`, { archived: false });

      // Refresh the department data from the response
      if (data.department) {
        setDepartments(prev => prev.map(d => d.id === id ? data.department : d));
      } else {
        setDepartments(prev => prev.map(d => d.id === id ? { ...d, archived: false, archivedBy: undefined, archivedOn: undefined } : d));
      }
      toast.success("Department unarchived successfully");
    } catch (error) {
      console.error("Error unarchiving department:", error);
      // toast.error("Failed to unarchive department");
    }
  };

  const archiveUser = async (id: string, userId: string) => {
    try {
      const data = await api.patch(`/api/users/${id}/archive`, { archived: true });

      // Refresh the user data from the response to get the populated archivedBy field
      if (data.user) {
        setUsers(prev => prev.map(u => u.id == id ? data.user : u));
      } else {
        setUsers(prev => prev.map(u => u.id == id ? { ...u, archived: true, archivedBy: userId, archivedOn: new Date().toISOString() } : u));
      }
      toast.success("User archived successfully");
    } catch (error: any) {
      console.error("Error archiving user:", error);
      if (error.message !== "Token expired") {
        // toast.error("Failed to archive user");
      }
    }
  };

  const unarchiveUser = async (id: string, userId: string) => {
    try {
      const data = await api.patch(`/api/users/${id}/archive`, { archived: false });

      // Refresh the user data from the response
      if (data.user) {
        setUsers(prev => prev.map(u => u.id == id ? data.user : u));
      } else {
        setUsers(prev => prev.map(u => u.id == id ? { ...u, archived: false, archivedBy: undefined, archivedOn: undefined } : u));
      }
      toast.success("User unarchived successfully");
    } catch (error) {
      console.error("Error unarchiving user:", error);
      // toast.error("Failed to unarchive user");
    }
  };

  // Training Plan functions
  const addTrainingPlan = async (plan: Partial<TrainingPlan>) => {
    try {
      const data = await api.post('/api/trainingplans', {
        trainingPlanName: plan.trainingPlanName,
        description: plan.description,
        procedureIds: plan.procedures?.map(proc => proc.id),
        traineeId: plan.traineeId,
        trainerId: plan.trainerId,
        sourceTemplateId: plan.sourceTemplateId
      });

      if (data.success) {
        setTrainingPlans(prev => [...prev, data.trainingPlan]);
        toast.success('Training plan created successfully');
      }
    } catch (error) {
      console.error('Error creating training plan:', error);
      // toast.error('Failed to create training plan');
      throw error;
    }
  };

  const updateTrainingProcedureCheck = async (planId: string, procedureId: string, updates: Partial<ProcedureCheck>, showToast = true) => {
    try {
      const data = await api.put(`/api/trainingplans/${planId}/procedure-check/${procedureId}`, { checks: updates });

      if (data.success) {
        setTrainingPlans(updateMainObj(trainingPlans, data.trainingPlan.id, data.trainingPlan));
        if (showToast) {
          toast.success('Training plan updated successfully');
        }
      }

    } catch (error: any) {
      console.error('Error updating training plan:', error);
      // toast.error(error.message);
    }
  };

  const updateTrainingPlan = async (id: string, updates: Partial<TrainingPlan>, showToast = true, isDelete = false) => {
    try {
      const payload: any = { ...updates };
      if (updates.procedures) {
        payload.procedureIds = updates.procedures.map(proc => proc.id);
        delete payload.procedures;
      }

      if (updates.trainee) {
        payload.traineeId = updates.trainee.id;
        delete payload.trainee;
      }

      if (updates.trainer) {
        payload.trainerId = updates.trainer.id;
        delete payload.trainer;
      }

      const data = await api.put(`/api/trainingplans/${id}`, payload);

      if (data.success) {
        if (isDelete) {
          setTrainingPlans(deleteFromMainObj(trainingPlans, data.trainingPlan.id));
        } else {
          setTrainingPlans(updateMainObj(trainingPlans, data.trainingPlan.id, data.trainingPlan));
        }

        if (showToast) {
          toast.success('Training plan updated successfully');
        }
      } else {
        toast.error(data.message);
      }
    } catch (error) {
      console.error('Error updating training plan:', error);
      // toast.error('Failed to update training plan');
      throw error;
    }
  };

  const addTrainingPlanTemplate = async (template: Partial<TrainingPlanTemplate>) => {
    try {
      const payload: any = { ...template };
      if (template.procedures) {
        payload.procedureIds = template.procedures.map(proc => proc.id);
        delete payload.procedures;
      }
      if (template.jobRole) {
        payload.jobRoleId = template.jobRole.id;
        delete payload.jobRole;
      }

      const data = await api.post('/api/trainingplan-templates', payload);
      if (data.success) {
        setTrainingPlanTemplates(prev => [...prev, mapTemplate(data.template)]);
        toast.success('Training plan template created successfully');
      }
    } catch (error: any) {
      console.error('Error adding training plan template:', error);
    }
  };

  const updateTrainingPlanTemplate = async (id: string, updates: Partial<TrainingPlanTemplate>) => {
    try {
      const payload: any = { ...updates };
      if (updates.procedures) {
        payload.procedureIds = updates.procedures.map(proc => proc.id);
        delete payload.procedures;
      }
      if (updates.jobRole) {
        payload.jobRoleId = updates.jobRole.id;
        delete payload.jobRole;
      }

      const data = await api.put(`/api/trainingplan-templates/${id}`, payload);
      if (data.success) {
        setTrainingPlanTemplates(updateMainObj(trainingPlanTemplates, data.template.id, mapTemplate(data.template)));
        toast.success('Training plan template updated successfully');
        // Refresh training plans as they might have been updated by sync logic
        fetchTrainingPlans();
      }
    } catch (error: any) {
      console.error('Error updating training plan template:', error);
    }
  };

  const archiveTrainingPlanTemplate = async (id: string, reason?: string) => {
    try {
      const data = await api.patch(`/api/trainingplan-templates/${id}/archive`, { archivedReason: reason });
      if (data.success) {
        setTrainingPlanTemplates(updateMainObj(trainingPlanTemplates, data.template.id, mapTemplate(data.template)));
        toast.success('Training plan template archived successfully');
      }
    } catch (error: any) {
      console.error('Error archiving training plan template:', error);
    }
  };

  const unarchiveTrainingPlanTemplate = async (id: string) => {
    try {
      const data = await api.patch(`/api/trainingplan-templates/${id}/unarchive`, {});
      if (data.success) {
        setTrainingPlanTemplates(updateMainObj(trainingPlanTemplates, data.template.id, mapTemplate(data.template)));
        toast.success('Training plan template unarchived successfully');
      }
    } catch (error: any) {
      console.error('Error unarchiving training plan template:', error);
    }
  };

  // Policy Management Functions
  const addPolicy = async (policy: Omit<Policy, "id" | "createdDate" | "lastUpdated" | "version" | "activityLog">) => {
    try {
      // Convert boolean requireSignature to string for API
      const apiPolicy = {
        ...policy,
        requireSignature: policy.requireSignature ? 'yes' : 'no'
      };

      const data = await api.post('/api/policies', apiPolicy);

      // setPolicies is now called with the response data
      setPolicies(prev => [data.policy, ...prev]);
      return data.policy;
    } catch (error) {
      console.error('Error creating policy:', error);
      // toast.error('Failed to create policy');
      throw error;
    }
  };

  const updatePolicy = async (id: string, updates: Partial<Policy>, showToast = true) => {
    try {
      // Convert string requireSignature to boolean for API if present
      const apiUpdates: any = { ...updates };
      if ('requireSignature' in updates) {
        apiUpdates.requireSignature = typeof updates.requireSignature === 'boolean'
          ? updates.requireSignature
          : updates.requireSignature === 'yes';
      }

      const data = await api.put(`/api/policies/${id}`, apiUpdates);

      setPolicies(prev => prev.map(p => p.id === id ? data.policy : p));
      if (showToast) {
        toast.success('Policy updated successfully');
      }
    } catch (error) {
      console.error('Error updating policy:', error);
      // toast.error('Failed to update policy');
      throw error;
    }
  };

  const updatePolicyInState = (id: string, updatedPolicy: Policy) => {
    setPolicies(prev => prev.map(p => p.id === id ? updatedPolicy : p));
  };

  const deletePolicy = async (id: string) => {
    try {
      await api.delete(`/api/policies/${id}`);

      setPolicies(prev => prev.filter(p => p.id !== id));
      toast.success('Policy deleted successfully');
    } catch (error) {
      console.error('Error deleting policy:', error);
      // toast.error('Failed to delete policy');
      throw error;
    }
  };

  const archivePolicy = async (id: string, reason?: string) => {
    try {
      await api.patch(`/api/policies/${id}/archive`, { reason });

      setPolicies(prev => prev.map(p => p.id == id ? { ...p, status: 'Archived' } : p));
      toast.success('Policy archived successfully');
      return true;
    } catch (error: any) {
      console.error('Error archiving policy:', error);
      // toast.error(data.message); // api helper toasts
      return false;
    }
  };

  const unarchivePolicy = async (id: string) => {
    try {
      await api.patch(`/api/policies/${id}/unarchive`, {});

      setPolicies(prev => prev.map(p => p.id === id ? { ...p, status: 'Active' } : p));
      toast.success('Policy unarchived successfully');
    } catch (error) {
      console.error('Error unarchiving policy:', error);
      // toast.error('Failed to unarchive policy');
      throw error;
    }
  };

  const getPoliciesForUser = (userId: string) => {
    // Get policies from assignments
    const userAssignments = policyAssignments.filter(assignment => assignment.user?.id == userId);

    // Extract policies from assignments (now policyId is populated with policy data)
    const assignedPolicies = userAssignments
      .map(assignment => {
        // Handle both old format (policyId as string) and new format (policyId as populated object)
        if (typeof assignment.policy?.id === 'string') {
          // Old format - find policy by ID
          return policies.find(policy => policy.id === assignment.policy?.id);
        } else {
          // New format - policy is already populated
          return assignment.policy?.id;
        }
      })
      .filter((policy): policy is Policy => policy !== undefined && policy.status === 'Active');

    return assignedPolicies;
  };

  const getMemosForUser = (userId: string) => {
    // Get memos from assignments
    const userMemoAssignments = memoAssignments.filter(assignment => assignment.user?.id == userId);

    // Extract memos from assignments (now memoId is populated with memo data)
    const assignedMemos = userMemoAssignments
      .map(assignment => {
        // Handle both old format (memoId as string) and new format (memoId as populated object)
        if (typeof assignment.memo?.id === 'string') {
          // Old format - find memo by ID
          return memos.find(memo => memo.id === assignment.memo?.id);
        } else {
          // New format - memo is already populated
          return assignment.memo?.id;
        }
      })
      .filter((memo): memo is Memo => memo !== undefined && memo.status === 'Active');

    return assignedMemos;
  };

  const getUserMemoSignature = (memoId: string, userId: string) => {
    // Find the assignment for this memo and user, then return the embedded signature
    // Handle cases where assignment.memo?.id might be an object (populated) vs string
    const assignment = memoAssignments.find(a => {
      const assignmentMemoId = typeof a.memo?.id === 'object' ? (a.memo?.id as any).id : a.memo?.id;
      return assignmentMemoId == memoId && a.user?.id == userId;
    });

    // const signature = assignment?.signature || null;
    return (assignment && assignment.signatureData && assignment.signedDate)
      ? {
        signatureType: assignment?.signatureType,
        signatureData: assignment?.signatureData,
        signedDate: assignment?.signedDate,
        ipAddress: assignment?.ipAddress,
        userAgent: assignment?.userAgent
      }
      : null;
  };

  const getMemoAssignments = (memoId: string) => {
    return memoAssignments.filter(assignment => {
      const assignmentMemoId = typeof assignment.memo?.id === 'object' ? (assignment.memo?.id as any).id : assignment.memo?.id;
      return assignmentMemoId === memoId;
    });
  };

  const getMemoById = (id: string) => {
    return memos.find(memo => memo.id === id);
  };

  const markMemoAsRead = async (memoId: string, userId: string) => {
    try {
      if (!memoId || !userId) {
        throw new Error('Memo ID and User ID are required');
      }

      const data = await api.post(`/api/memos/${memoId}/read`, {});
      console.log('[DataContext] markMemoAsRead success:', data);

      if (data.success) {
        // Refresh assignments to get the latest state
        await refreshUserAssignments();
      }
    } catch (error) {
      console.error('[DataContext] Error marking memo as read:', error);
      throw error;
    }
  };

  const signMemo = async (memoId: string, signatureData: { signatureType: 'Draw' | 'Type'; signatureData: string }) => {
    try {
      if (!memoId || !signatureData.signatureData) {
        throw new Error('Memo ID and signature data are required');
      }

      const data = await api.post(`/api/memos/${memoId}/sign`, signatureData);
      console.log('[DataContext] signMemo success:', data);

      if (data.success) {
        // Refresh user assignments and signatures to get the latest state
        await refreshUserAssignments();
      }
    } catch (error) {
      console.error('[DataContext] Error signing memo:', error);
      throw error;
    }
  };

  const markPolicyAsRead = async (policyId: string, userId: string) => {
    try {
      if (!policyId || !userId) {
        throw new Error('Policy ID and User ID are required');
      }

      const data = await api.post(`/api/policies/${policyId}/read`, {});

      if (data.success) {
        // Refresh assignments from backend to get the latest state
        console.log('[DataContext] Refreshing policy assignments after read...');

        // Add a small delay to ensure backend has saved the assignment
        await new Promise(resolve => setTimeout(resolve, 100));

        try {
          const assignmentsData = await api.get('/api/policies/user/my-policies');

          if (assignmentsData.success) {
            console.log('[DataContext] Updated policy assignments after read:', {
              count: assignmentsData.assignments?.length || 0,
              assignments: assignmentsData.assignments
            });

            setPolicyAssignments(assignmentsData.assignments || []);
          }
        } catch (refreshError) {
          console.error('[DataContext] Error refreshing assignments after read:', refreshError);
          // Fallback handled by reloading
        }
      }
    } catch (error) {
      console.error('[DataContext] Error marking policy as read:', error);
      // Don't show toast error here - let the calling component handle it
      throw error;
    }
  };

  const signPolicy = async (policyId: string, signatureData: { signatureType: 'Draw' | 'Type'; signatureData: string }) => {
    try {
      console.log('[DataContext] signPolicy called with:', {
        policyId,
        signatureType: signatureData.signatureType,
        signatureDataLength: signatureData.signatureData?.length,
        hasToken: !!token,
        tokenPreview: token?.substring(0, 10) + '...',
        currentUser: currentUser?.id
      });

      const data = await api.post(`/api/policies/${policyId}/sign`, signatureData);

      console.log('[DataContext] signPolicy response data:', data);

      if (data.success) {
        // Instead of updating frontend state directly, refresh assignments to get real data
        // This prevents issues with duplicate assignments and ensures data consistency
        if (currentUser) {
          console.log('[DataContext] Refreshing assignments after successful signature');
          await refreshUserAssignments();
        }

        console.log('[DataContext] Policy signed successfully');
        // Component handles success toast, no need for duplicate here
      } else {
        console.error('[DataContext] Sign policy failed - response not successful:', data);
        throw new Error(data.message || 'Failed to sign policy');
      }
    } catch (error) {
      console.error('[DataContext] Error signing policy:', error);
      console.error('[DataContext] Error details:', {
        message: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : 'No stack trace',
        policyId,
        signatureData: {
          type: signatureData.signatureType,
          hasData: !!signatureData.signatureData
        }
      });
      toast.error('Failed to sign policy');
      throw error;
    }
  };

  const getUserPolicySignature = (policyId: string, userId: string) => {
    // Find the assignment for this user and policy
    // Note: policyId might be populated as an object, so we need to handle both cases
    const assignment = policyAssignments.find(a => {
      const assignmentPolicyId = typeof a.policy?.id === 'object' ? (a.policy?.id as any).id : a.policy?.id;
      return assignmentPolicyId == policyId && a.user?.id == userId;
    });

    // Return the embedded signature only if it has actual signature data
    return (assignment && assignment.signatureData && assignment.signedDate)
      ? {
        signatureType: assignment?.signatureType,
        signatureData: assignment?.signatureData,
        signedDate: assignment?.signedDate,
        ipAddress: assignment?.ipAddress,
        userAgent: assignment?.userAgent
      }
      : null;
  };

  const getPolicyAssignments = (policyId: string) => {
    return policyAssignments.filter(assignment => {
      const assignmentPolicyId = typeof assignment.policy?.id === 'object' ? (assignment.policy?.id as any).id : assignment.policy?.id;
      return assignmentPolicyId === policyId;
    });
  };

  // Policy signatures are now embedded in assignments - no separate function needed

  // Helper functions
  const getDepartmentName = (id: string) => {
    const department = departments.find(dept => dept.id === id);
    return department ? department.departmentName : 'Unknown Department';
  };

  const getUserName = (id: string | any) => {
    if (!id) {
      return 'No User Specified';
    }

    // Handle case where a populated user object is passed instead of just ID
    if (typeof id === 'object' && id.name) {
      return id.name;
    }

    const userId = typeof id === 'string' ? id : id.id || id.toString();
    const user = users.find(u => u.id === userId);

    if (!user) {
      // Check if the current user is the creator (fallback for missing user data)
      if (currentUser && currentUser.id === userId) {
        return currentUser.name;
      }

      // If users array is empty, it might still be loading
      if (users.length === 0) {
        return 'Loading...';
      }

      return 'Unknown';
    }

    return user.name;
  };

  const getPolicyById = (id: string) => {
    return policies.find(policy => policy.id == id);
  };

  const getUserById = (id: string) => {
    return users.find(user => user.id == id);
  };

  const getDepartmentByCode = (code: string) => {
    return departments.find(dept => dept.departmentCode == code);
  };

  const getUserPolicySignatures = (userId: string) => {
    // Get all assignments for this user that have actual signatures
    return policyAssignments
      .filter(assignment => assignment.user?.id == userId && assignment && assignment.signatureData && assignment.signedDate)
      .map(assignment => {
        return {
          signatureType: assignment?.signatureType,
          signatureData: assignment?.signatureData,
          signedDate: assignment?.signedDate,
          ipAddress: assignment?.ipAddress,
          userAgent: assignment?.userAgent
        }
      });
  };

  const addUsersToPolicy = async (policyId: string, userIds: string[]) => {
    try {
      const data = await api.post(`/api/policies/${policyId}/add-users`, { userIds });

      if (data.success) {
        // Refresh assignments to get real data instead of creating temporary ones
        await refreshUserAssignments();
        toast.success(`${data.addedUsers} user(s) successfully added to the policy`);
      }
    } catch (error) {
      console.error('Error adding users to policy:', error);
      // toast.error('Failed to add users to policy');
      throw error;
    }
  };

  const addUsersToMemo = async (memoId: string, userIds: string[]) => {
    try {
      const data = await api.post(`/api/memos/${memoId}/add-users`, { userIds });

      if (data.success) {
        // Refresh assignments to get real data instead of creating temporary ones
        await refreshUserAssignments();
        toast.success(`${data.addedUsers} user(s) successfully added to the memo`);
      }
    } catch (error) {
      console.error('Error adding users to memo:', error);
      // toast.error('Failed to add users to memo');
      throw error;
    }
  };

  const removeUsersFromPolicy = async (policyId: string, userIds: string[]) => {
    try {
      const data = await api.delete(`/api/policies/${policyId}/remove-users`, { body: JSON.stringify({ userIds }) });

      if (data.success) {
        // Backend handles all assignment logic, just refresh the assignment data
        // await fetchPolicyAssignments(policyId);
        await refreshUserAssignments();
        toast.success(data.message);
      } else {
        toast.error(data.message);
      }
    } catch (error) {
      console.error('Error removing user from policy:', error);
      // toast.error('Failed to remove user from policy');
      throw error;
    }
  };

  const removeUsersFromMemo = async (memoId: string, userIds: string[]) => {
    try {
      const data = await api.delete(`/api/memos/${memoId}/remove-users`, { body: JSON.stringify({ userIds }) });

      if (data.success) {
        await refreshUserAssignments();
        toast.success(data.message);
      } else {
        toast.error(data.message);
      }
    } catch (error) {
      console.error('Error removing user from memo:', error);
      // toast.error(errorMessage);
      throw error;
    }
  };

  const fetchPolicyAssignments = async (policyId: string) => {
    try {
      // For admin assignment management, we need policy-specific assignments (all users assigned to this policy)
      // This is different from user-specific assignments loaded on app init
      const existingAssignments = policyAssignments.filter(assignment => assignment.policy?.id === policyId);

      // Check if we have policy-specific assignment data by looking for assignments with proper structure
      // User-specific assignments from initial load might not have the full assignment management data
      const hasPolicySpecificData = existingAssignments.length > 0 &&
        existingAssignments.some(a => a.assignedDate && a.hasOwnProperty('remindersSent'));

      if (hasPolicySpecificData) {
        console.log(`[DataContext] Policy-specific assignments for ${policyId} already loaded, skipping fetch`);
        return;
      }

      console.log(`[DataContext] Fetching policy-specific assignments for ${policyId}`);
      console.log(`[DataContext] Current context has ${existingAssignments.length} assignments, but need policy-specific data`);

      const data = await api.get(`/api/policies/${policyId}/assignments`);

      if (data.success) {
        console.log(`[DataContext] Received ${data.assignments?.length || 0} policy-specific assignments for policy ${policyId}`);
        // Update assignments in state with the fetched data for this policy
        setPolicyAssignments(prev => {
          // Remove existing assignments for this policy and add the new ones
          const filtered = prev.filter(assignment => assignment.policy?.id !== policyId);
          return [...filtered, ...(data.assignments || [])];
        });
      }
    } catch (error) {
      console.error('Error fetching policy assignments:', error);
    }
  };

  // Policy signatures are now embedded in assignments - no separate fetch needed

  // Force refresh functions for assignment data
  const refreshPolicyAssignments = async (policyId: string) => {
    try {
      console.log(`[DataContext] Force refreshing policy assignments for ${policyId}`);
      const data = await api.get(`/api/policies/${policyId}/assignments`);

      if (data.success) {
        console.log(`[DataContext] Force refresh: Received ${data.assignments?.length || 0} assignments for policy ${policyId}`);
        // Update assignments in state with the fetched data for this policy
        setPolicyAssignments(prev => {
          // Remove existing assignments for this policy and add the new ones
          const filtered = prev.filter(assignment => assignment.policy?.id !== policyId);
          return [...filtered, ...(data.assignments || [])];
        });
      }
    } catch (error) {
      console.error('Error force refreshing policy assignments:', error);
    }
  };

  // Policy signatures are now embedded in assignments - no separate refresh needed

  // Assignment Management Functions
  const getPoliciesForAssignment = async () => {
    try {
      const data = await api.get('/api/policies/for-assignment');
      return data.policies || [];
    } catch (error) {
      console.error('Error fetching policies for assignment:', error);
      throw error;
    }
  };



  const createPolicyAssignments = async (policyId: string, userIds: string[], assignmentType?: string) => {
    try {
      const data = await api.post('/api/policies/assignments', { policyId, userIds, assignmentType });

      if (data.success) {
        // Don't add temporary assignments to state - let refreshUserAssignments handle it
        // This prevents duplicate assignments and maintains data integrity
        toast.success(data.message);

        // Refresh user assignments to get the real data from backend
        await refreshUserAssignments();
      }
    } catch (error) {
      console.error('Error creating policy assignments:', error);
      throw error;
    }
  };

  const getAssignmentSummary = async (policyId: string) => {
    try {
      const data = await api.get(`/api/policies/${policyId}/assignment-summary`);
      return data;
    } catch (error) {
      console.error('Error fetching assignment summary:', error);
      throw error;
    }
  };

  const getAllPolicyAssignments = async (filters?: { policyId?: string; userId?: string; status?: string }) => {
    try {
      const queryParams = new URLSearchParams();
      if (filters?.policyId) queryParams.append('policyId', filters.policyId);
      if (filters?.userId) queryParams.append('userId', filters.userId);
      if (filters?.status && filters.status !== 'all') queryParams.append('status', filters.status);

      const queryString = queryParams.toString() ? '?' + queryParams.toString() : '';
      const data = await api.get(`/api/policies/all-assignments${queryString}`);

      return data.assignments || [];
    } catch (error) {
      console.error('[DataContext] Error fetching all assignments:', error);
      throw error;
    }
  };

  // ==================== MEMO MANAGEMENT FUNCTIONS ====================

  const addMemo = async (memo: Omit<Memo, 'id' | 'createdOn' | 'lastUpdated' | 'activityLog' | 'archived' | 'deleted'>): Promise<Memo> => {
    try {
      const data = await api.post('/api/memos', memo);

      if (data.success) {
        setMemos(prev => [data.memo, ...prev]);
        return data.memo;
      }
      throw new Error('Failed to create memo');
    } catch (error) {
      console.error('Error creating memo:', error);
      throw error;
    }
  };

  const updateMemo = async (id: string, updates: Partial<Memo>) => {
    try {
      const data = await api.put(`/api/memos/${id}`, updates);

      if (data.success) {
        setMemos(prev => prev.map(memo => memo.id === id ? { ...memo, ...updates } : memo));
        toast.success('Memo updated successfully');
      }
    } catch (error) {
      console.error('Error updating memo:', error);
      toast.error('Failed to update memo');
      throw error;
    }
  };

  const deleteMemo = async (id: string) => {
    try {
      const data = await api.delete(`/api/memos/${id}`);

      if (data.success) {
        setMemos(prev => prev.filter(memo => memo.id !== id));
        toast.success('Memo deleted successfully');
      }
    } catch (error) {
      console.error('Error deleting memo:', error);
      toast.error('Failed to delete memo');
      throw error;
    }
  };

  // Function to refresh memos
  const refreshMemos = async () => {
    try {
      const memosData = await api.get('/api/memos');
      if (memosData.success) {
        // Filter out undefined memos before setting state
        const validMemos = (memosData.memos || []).filter((memo: any) => memo != null);
        setMemos(validMemos);
      }
    } catch (error) {
      console.error("Error refreshing memos:", error);
    }
  };

  // Function to refresh policies
  const refreshPolicies = async () => {
    try {
      const policiesData = await api.get('/api/policies');
      if (policiesData.success) {
        // Filter out undefined policies before setting state
        const validPolicies = (policiesData.policies || []).filter((policy: any) => policy != null);
        setPolicies(validPolicies);
      }
    } catch (error) {
      console.error("Error refreshing policies:", error);
    }
  };

  const fetchMemoAssignments = async (memoId: string) => {
    try {
      const data = await api.get(`/api/memos/${memoId}/assignments`);

      // setMemoAssignments(prev => [...prev.filter(a => a.memo?.id !== memoId), ...data.assignments]);
      setMemoAssignments(prev => {
        // Filter out any assignments for this memo, then add the new ones
        const otherAssignments = prev.filter(a => {
          // Handle both object and string id formats
          const aMemoId = typeof a.memo?.id === 'object' ? (a.memo?.id as any).id : a.memo?.id;
          return aMemoId !== memoId;
        });
        return [...otherAssignments, ...data.assignments];
      });
    } catch (error) {
      console.error('Error fetching memo assignments:', error);
      // toast.error('Failed to fetch memo assignments');
    }
  };



  const fetchMemos = async () => {
    try {
      const data = await api.get('/api/memos?includeDeleted=true');
      if (data.success) {
        // Filter out undefined memos before setting state
        const validMemos = (data.memos || []).filter((memo: any) => memo != null);
        setMemos(validMemos);
      }
    } catch (error) {
      console.error('Error fetching memos:', error);
      toast.error('Failed to load memos');
    }
  };

  const refreshUserAssignments = useCallback(async () => {
    try {
      console.log('[DataContext] Refreshing user assignments...');

      // Fetch policy assignments
      try {
        const assignmentsData = await api.get('/api/policies/user/my-policies', {
          skipAuthHandler: true
        });

        console.log('[DataContext] Refreshed policy assignments data:', assignmentsData);

        if (assignmentsData.success) {
          console.log('[DataContext] Setting refreshed policy assignments:', {
            count: assignmentsData.assignments?.length || 0,
            assignments: assignmentsData.assignments
          });
          setPolicyAssignments(assignmentsData.assignments || []);
        } else {
          console.log('[DataContext] Policy refresh response not successful');
          setPolicyAssignments([]);
        }
      } catch (policyError) {
        console.warn('[DataContext] Failed to refresh policy assignments:', policyError);
        // If 401, we might not want to clear data if we are just offline? 
        // But api helper handles 401 by redirecting, so we probably just want to log.
      }


      // Fetch memo assignments
      try {
        const memoAssignmentsData = await api.get('/api/memos/user/my-memos');

        console.log('[DataContext] Refreshed memo assignments data:', memoAssignmentsData);

        if (memoAssignmentsData.success) {
          console.log('[DataContext] Setting refreshed memo assignments:', {
            count: memoAssignmentsData.assignments?.length || 0,
            assignments: memoAssignmentsData.assignments
          });
          setMemoAssignments(memoAssignmentsData.assignments || []);
        } else {
          console.log('[DataContext] Memo refresh response not successful');
          setMemoAssignments([]);
        }
      } catch (memoError) {
        console.log('[DataContext] Memo assignments endpoint not available or error:', memoError);
        // Don't fail the whole refresh if memo endpoint is not available
      }


    } catch (error) {
      console.error('[DataContext] Error refreshing user assignments:', error);
    }
  }, []); // Empty dependency array since we only need API_BASE which is constant

  const value = {
    departments,
    users,
    systems,
    activities,
    activitiesData,
    procedures,
    trainings,
    jobRoles,
    trainingPlans,
    trainingPlanTemplates,
    externalPartners,
    policies,
    policyAssignments,
    memos,
    approvers,
    managerUsers,
    setMemos,
    fetchManagerUsers,
    fetchProcedures,

    memoAssignments,
    refreshMemos,
    refreshPolicies,
    addUser,
    updateUser,
    deleteUser,
    archiveUser,
    unarchiveUser,
    addDepartment,
    updateDepartment,
    deleteDepartment,
    assignDepartmentManager,
    archiveDepartment,
    unarchiveDepartment,
    addSystem,
    updateSystem,
    deleteSystem,
    searchActivities,
    addActivity,
    addActivityLink,
    updateActivity,
    updateActivityLink,
    deleteActivity,
    deleteActivityLink,
    addProcedure,
    updateProcedure,
    deleteProcedureLink,
    deleteProcedure,
    addTraining,
    updateTraining,
    deleteTraining,
    mapJobRole,
    addJobRole,
    updateJobRole,
    deleteJobRole,
    archiveJobRole,
    unarchiveJobRole,
    archiveProcedure,
    unarchiveProcedure,
    assignUserDepartments,
    addTrainingPlan,
    updateTrainingPlan,
    addTrainingPlanTemplate,
    updateTrainingPlanTemplate,
    archiveTrainingPlanTemplate,
    unarchiveTrainingPlanTemplate,
    updateTrainingProcedureCheck,
    addPolicy,
    updatePolicy,
    updatePolicyInState,
    deletePolicy,
    archivePolicy,
    unarchivePolicy,
    getPoliciesForUser,
    markPolicyAsRead,
    signPolicy,
    addUsersToPolicy,
    addUsersToMemo,
    removeUsersFromPolicy,
    removeUsersFromMemo,
    fetchPolicyAssignments,
    refreshPolicyAssignments,
    getUserPolicySignature,
    getPolicyAssignments,
    getPoliciesForAssignment,
    createPolicyAssignments,
    getAssignmentSummary,
    getAllPolicyAssignments,
    getDepartmentName,
    getUserName,
    getPolicyById,
    getUserById,
    getDepartmentByCode,
    getUserPolicySignatures,
    addMemo,
    updateMemo,
    deleteMemo,
    fetchMemoAssignments,

    fetchMemos,
    refreshUserAssignments,
    getMemosForUser,
    getUserMemoSignature,
    getMemoAssignments,
    getMemoById,
    markMemoAsRead,
    signMemo,
    fetchApprovers,

    // Qualifications
    fetchQualifications,
    addQualification,
    updateQualification,
    deleteQualification,
    restoreQualification,
    fetchQualificationTypes,
    qualificationGroups,
    fetchQualificationGroups,
    qualificationPresets,
    fetchQualificationPresets,
    qualifications,
    qualificationTypes,
    selectionUsers,
    fetchSelectionUsers,
    employmentEntities,
    fetchEmploymentEntities,
    userRoles,
    fetchUserRoles,
    jobTitles,
    fetchJobTitles,
    addJobTitle,
    updateJobTitle,
    deleteJobTitle,
    jobRoleLocations,
    fetchJobRoleLocations,
    addJobRoleLocation,
    updateJobRoleLocation,
    deleteJobRoleLocation,
    jobGrades,
    fetchJobGrades,
    addJobGrade,
    updateJobGrade,
    deleteJobGrade
  };

  return (
    <DataContext.Provider value={value}>
      {children}
    </DataContext.Provider>
  );
};

// Do not export default DataContext
// export default DataContext;